import { CloseOutlined } from '@mui/icons-material'
import {
    Box,
    Button,
    debounce,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Icon,
    IconButton,
    InputAdornment,
    Switch,
    TextField,
    Typography,
} from '@mui/material'
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import {
    MRT_ColumnDef,
    MRT_ColumnFiltersState,
    MRT_SortingState,
} from 'material-react-table'
import { useInfiniteQuery } from '@tanstack/react-query'
import SearchIcon from '@mui/icons-material/Search'
import { useAppDispatch, useAppSelector } from '../../../redux/hook'
import CheckboxInput from '../../Common/CheckboxInput'
import MRTTable from '../../Common/Table'
import {
    fetchSharedOrgsByTemId,
    updatedSharedOrgs,
} from '../../../services/dtServices'
import { updateLoader } from '../../../redux/slices/loaderSlice'
import { useTranslation } from 'react-i18next'
export interface TemplateSharedOrgs {
    id: string
    name: string
    shared: boolean
}

export interface TemplateSharedOrgsApiResponse {
    totalCount: number
    organizations: TemplateSharedOrgs[]
}
export interface PostTemplateSharedOrgsApi {
    shareOrganizations: string[]
    unshareOrganizations: string[]
}
interface ShareTemplatePropsType {
    seletedTemplate: string
    isOpen: boolean
    onClose: any
}

const ShareTemplate = ({
    seletedTemplate,
    isOpen,
    onClose,
}: ShareTemplatePropsType): ReactElement => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const org = useAppSelector((state) => state.org.org)

    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
        [],
    )

    const [globalFilter, setGlobalFilter] = useState<string>()
    const [sorting, setSorting] = useState<MRT_SortingState>([])
    const [, setIsFilterOpen] = useState<boolean>(false)

    const [selectedOrgList, setSeletedOrgList] = useState<string[]>([])
    const [unSelectedOrgList, setUnSeletedOrgList] = useState<string[]>([])

    const [includeVirtualOrgs, setIncludeVirtualOrgs] = useState<boolean>(false)

    const dropdownRef = useRef(null)

    const handleClickOutside = (event: any) => {
        const container = dropdownRef.current ?? document.body // Fallback to body
        if (container.contains(event.target)) {
            // nothing
        } else {
            setIsFilterOpen(false)
        }
    }

    const fetchSize = 10

    const columns: MRT_ColumnDef<TemplateSharedOrgs>[] = [
        {
            accessorKey: 'name',
            header: t('name'),
            Cell: (row: any) => (
                <CheckboxInput
                    id={row?.row?.original?.id}
                    label={row?.row?.original?.name}
                    value={
                        (selectedOrgList.includes(row?.row?.original?.id) ||
                            row?.row?.original?.shared) &&
                        !unSelectedOrgList.includes(row?.row?.original?.id)
                    }
                    onChange={(id, value) => {
                        handleShareChange(id, value, row?.row?.original?.shared)
                    }}
                />
            ),
        },
    ]

    const { data, fetchNextPage, isError, isFetching, isLoading } =
        useInfiniteQuery<TemplateSharedOrgsApiResponse>({
            queryKey: [
                'organizations-table-data',
                columnFilters, // refetch when columnFilters changes
                globalFilter, // refetch when globalFilter changes
                sorting, // refetch when sorting changes
                seletedTemplate,
                includeVirtualOrgs,
            ],
            queryFn: async ({ pageParam }) => {
                let sortBy = sorting[0]?.id
                const sortOrder = sortBy
                    ? sorting[0]?.desc
                        ? 'DESC'
                        : 'ASC'
                    : ''
                if (sortBy === 'userCount') {
                    sortBy = 'USER'
                }
                if (sortBy === 'Created At') {
                    sortBy = 'CREATED'
                }
                if (sortBy === 'parentOrganizationName') {
                    sortBy = 'PARENT_ORG'
                }
                const response = await fetchSharedOrgsByTemId(
                    seletedTemplate,
                    org?.id || '',
                    {
                        pageNumber: (pageParam as number) + 1,
                        size: fetchSize,
                        searchText: globalFilter,
                        sortBy: sortBy ? sortBy.toUpperCase() : '',
                        sortOrder,
                        filterBy: '',
                        includeVirtualOrgs,
                    },
                )
                return response as TemplateSharedOrgsApiResponse
            },
            initialPageParam: 0,
            getNextPageParam: (_lastGroup, groups) => groups.length,
            refetchOnWindowFocus: false,
        })
    const flatData = useMemo(
        () => data?.pages.flatMap((page) => page.organizations) ?? [],
        [data],
    )

    const totalDBRowCount = data?.pages?.[0]?.totalCount ?? 0

    const handleShareChange = debounce(
        (id: string, value: boolean, preValue: boolean) => {
            if (value) {
                if (!preValue) {
                    setSeletedOrgList((prev) => [...prev, id])
                }
                setUnSeletedOrgList((prev) => prev.filter((i) => i !== id))
            } else {
                if (preValue) {
                    setUnSeletedOrgList((prev) => [...prev, id])
                }
                setSeletedOrgList((prev) => prev.filter((i) => i !== id))
            }
        },
        300,
    )

    const onCloseDialog = () => {
        onClose()
    }

    const onSaveDailog = async () => {
        dispatch(updateLoader(true))
        const payload: PostTemplateSharedOrgsApi = {
            shareOrganizations: selectedOrgList,
            unshareOrganizations: unSelectedOrgList,
        }
        await updatedSharedOrgs(seletedTemplate, org?.id || '', payload)
        dispatch(updateLoader(false))
        onClose()
    }

    // if (isLoading && isFetching) {
    //     return <LoadingModal open={true} />
    // }

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside)
        return () =>
            document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    return (
        <Dialog
            open={isOpen}
            scroll="paper"
            PaperProps={{
                sx: {
                    width: '550px',
                    minHeight: '710px',
                    maxHeight: '710px',
                },
            }}
            aria-labelledby="share-template-dialog-title"
            aria-describedby="share-template-dialog-description"
            sx={{ padding: '10px 30px' }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                }}
            >
                <DialogTitle
                    id="share-template-dialog-title"
                    sx={{ padding: '0px' }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        padding="15px"
                        paddingLeft="20px"
                    >
                        <Typography
                            id="modal-modal-title"
                            variant="h6"
                            component="h2"
                            sx={{ paddingTop: '10px' }}
                        >
                            {t('shareEntityWithOrgs')}
                        </Typography>
                        <Icon
                            onClick={() => onCloseDialog()}
                            sx={{ marginLeft: 'auto' }}
                            fontSize="small"
                            id="share-template-close-icon"
                            aria-label={t('close')}
                            title={t('close')}
                        >
                            <CloseOutlined
                                sx={{
                                    color: '#000',
                                    marginTop: '-2px',
                                }}
                            />
                        </Icon>
                    </Box>
                </DialogTitle>
                <DialogContent
                    sx={{
                        height: 'auto',
                        paddingBottom: '0px',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '20px',
                        }}
                    >
                        <Box
                            sx={{
                                background: '#E3F4FF',
                                borderRadius: '25px',
                                width: '300px',
                            }}
                        >
                            <TextField
                                id="share-template-search-bar"
                                sx={{
                                    padding: 0,
                                    '& fieldset': { border: 'none' },
                                }}
                                placeholder={t('search')}
                                value={globalFilter}
                                onChange={(e) =>
                                    setGlobalFilter(e.target.value)
                                }
                                className="table-search-bar"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IconButton
                                                sx={{
                                                    '&:hover ': {
                                                        background: 'none',
                                                        cursor: 'auto',
                                                    },
                                                }}
                                                id="share-template-search-icon"
                                                aria-label={t('search')}
                                                title={t('search')}
                                            >
                                                <SearchIcon
                                                    sx={{ color: 'primary' }}
                                                />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    style: {
                                        height: 36,
                                        paddingLeft: '5px',
                                    },
                                }}
                                inputProps={{
                                    'aria-label': t('search'),
                                    title: t('search'),
                                }}
                            />
                        </Box>
                        <Box sx={{ marginLeft: '30px' }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        id="includeVirtualOrgs"
                                        checked={includeVirtualOrgs}
                                        onChange={(e: any) =>
                                            setIncludeVirtualOrgs(
                                                e.target.checked,
                                            )
                                        }
                                        name={t('virtualOrganizations')}
                                    />
                                }
                                label={t('virtualOrganizations')}
                                sx={{
                                    marginRight: '0px',
                                    '& .MuiFormControlLabel-label': {
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                        whiteSpace: 'nowrap',
                                    },
                                }}
                            ></FormControlLabel>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                marginLeft: '10px',
                                position: 'relative',
                            }}
                            ref={dropdownRef}
                        >
                            {/* <Icon
                                onClick={() => setIsFilterOpen(!isFilterOpen)}
                                sx={{ marginLeft: 'auto' }}
                                fontSize="small"
                            >
                                <FilterListOutlined
                                    sx={{
                                        color: theme.palette.primary.main,
                                        marginTop: '-2px',
                                    }}
                                />
                            </Icon> */}
                            {/* {isFilterOpen ? (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        background: '#fff',
                                        zIndex: '111',
                                        minWidth: '130px',
                                        left: '30px',
                                        top: '0px',
                                        boxShadow:
                                            '0px 3px 6px 0px rgba(0,0,0,0.4)',
                                        '& div:not(:first-of-type)': {
                                            padding: '8px 15px',
                                            borderTop:
                                                '1px solid rgba(0,0,0,0.2)',
                                        },
                                        borderRadius: '3px',
                                    }}
                                >
                                    <Box sx={{ padding: '7px 17px' }}>
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                fontWeight: '700',
                                            }}
                                        >
                                            Filter
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <CheckboxInput
                                            id="share-org-filters-read"
                                            label={'Read'}
                                            value={filterList?.includes(
                                                TemplatePermissionMode.READ,
                                            )}
                                            onChange={(id, value) => {
                                                if (value) {
                                                    setFilterList((prev) => [
                                                        ...prev,
                                                        TemplatePermissionMode.READ,
                                                    ])
                                                } else {
                                                    setFilterList((prev) =>
                                                        prev.filter(
                                                            (i) =>
                                                                i !==
                                                                TemplatePermissionMode.READ,
                                                        ),
                                                    )
                                                }
                                            }}
                                        />
                                    </Box>
                                    <Box>
                                        <CheckboxInput
                                            id="share-org-filters-read-write"
                                            label={'Write'}
                                            value={filterList?.includes(
                                                TemplatePermissionMode.READ_WRITE,
                                            )}
                                            onChange={(id, value) => {
                                                if (value) {
                                                    setFilterList((prev) => [
                                                        ...prev,
                                                        TemplatePermissionMode.READ_WRITE,
                                                    ])
                                                } else {
                                                    setFilterList((prev) =>
                                                        prev.filter(
                                                            (i) =>
                                                                i !==
                                                                TemplatePermissionMode.READ_WRITE,
                                                        ),
                                                    )
                                                }
                                            }}
                                        />
                                    </Box>
                                </Box>
                            ) : null} */}
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Box
                            sx={{
                                width: 'calc(100%)',
                                height: '500px',
                            }}
                        >
                            <MRTTable<
                                TemplateSharedOrgs,
                                TemplateSharedOrgsApiResponse
                            >
                                columns={columns}
                                sorting={sorting}
                                globalFilter={globalFilter}
                                columnFilters={columnFilters}
                                setColumnFilters={setColumnFilters}
                                setGlobalFilter={setGlobalFilter}
                                setSorting={setSorting}
                                data={flatData}
                                totalDBRowCount={totalDBRowCount}
                                fetchNextPage={fetchNextPage}
                                isError={isError}
                                isFetching={isFetching}
                                isLoading={isLoading}
                                enableBottomToolbar={false}
                                isCurvedBottomToolbar={false}
                                enableRowSelection={false}
                                maxHeight={'500px'}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        padding: '20px 25px 20px 0px',
                    }}
                >
                    <Button
                        onClick={() => onCloseDialog()}
                        sx={{
                            fontSize: '12px',
                            padding: '7px 20px',
                            marginRight: '15px',
                        }}
                        variant="outlined"
                        id="share-template-cancel"
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            onSaveDailog()
                        }}
                        sx={{
                            width: '100px',
                            color: '#fff',
                            fontSize: '12px',
                            padding: '7px 20px',
                        }}
                        id="share-template-share"
                    >
                        {t('share')}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    )
}

export default ShareTemplate
